/* eslint-disable quotes */
/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  message,
  Tag,
  Input,
  Space,
  Tooltip,
  Card,
  Select,
  Modal,
  Form,
  Spin,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import Heading from "../../components/Heading";
import TableContainer from "../../components/TableContainer";
import { daApiService } from "../../api/DAApi";
import { hotspotApiService } from "../../api/HotspotApi";
import "./style.scss";
import urls from "../../api/urls";
import API from "../../api";
import constants from "../../constants/constants";

const { Search } = Input;
const { Option } = Select;
const { DA_TYPES } = constants;

/**
 * DAHotspotManagement - Main component for DA Hotspot Assignment
 * Follows Single Responsibility Principle - handles only DA list management and hotspot assignment
 */
const DAHotspotManagement = () => {
  // State management
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Column filters state
  const [columnFilters, setColumnFilters] = useState({
    deliveryAgentId: "",
    name: "",
    mobile: "",
    vehicleType: "",
    status: "",
  });

  // Modal and edit state
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedDA, setSelectedDA] = useState(null);
  const [hotspotsList, setHotspotsList] = useState([]);
  const [hotspotLoading, setHotspotLoading] = useState(false);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [deliveryPartnerLoading, setDeliveryPartnerLoading] = useState(false);
  const [form] = Form.useForm();

  // Load data on component mount
  useEffect(() => {
    fetchDAList();
  }, [currentPage, pageSize]);

  // Handle form population after hotspots list is loaded
  useEffect(() => {
    if (hotspotsList.length > 0 && selectedDA && isEditModalVisible) {
      if (
        selectedDA.assignedHotspot &&
        Array.isArray(selectedDA.assignedHotspot) &&
        selectedDA.assignedHotspot.length > 0
      ) {
        const existingHotspot = selectedDA.assignedHotspot[0];
        // Use the 'id' field which is the actual hotspot ID, not '_id' which is the assignment record ID
        const hotspotId = existingHotspot?.id || existingHotspot?.hotspotId;

        // Check if the hotspot exists in the loaded list
        const foundHotspot = hotspotsList.find(
          (h) => h._id === hotspotId || h.id === hotspotId
        );

        if (foundHotspot && hotspotId) {
          form.setFieldsValue({
            hotspotId: hotspotId,
          });
        } else {
          form.setFieldsValue({
            hotspotId: null,
          });
        }
      }

      // Set Delivery Partner
      if (selectedDA.dpId) {
        form.setFieldsValue({
          dpId: selectedDA.dpId,
        });
      } else {
        form.setFieldsValue({
          dpId: null,
        });
      }

      if (selectedDA.daType) {
        form.setFieldsValue({
          daType: selectedDA.daType,
        });
      } else {
        form.setFieldsValue({
          daType: null,
        });
      }
    }
  }, [hotspotsList, selectedDA, isEditModalVisible, form]);

  // Debounced search with column filters
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1);
      fetchDAList();
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, columnFilters]);

  // Fetch delivery partners when modal opens
  useEffect(() => {
    if (isEditModalVisible) {
      fetchDeliveryPartners();
    }
  }, [isEditModalVisible]);

  /**
   * Fetch delivery agents list from API
   */
  const fetchDAList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage.toString(),
        limit: pageSize.toString(),
        ...(searchTerm && { search: searchTerm }),
      };

      // Add column filters to params
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value && value.trim() !== "") {
          params[key] = value.trim();
        }
      });

      const response = await daApiService.getAllDeliveryAgents(params);

      if (response.success) {
        setTableData(response.data || []);
        setTotal(response.totalRecords || 0);
      } else {
        message.error(response.message || "Failed to fetch delivery agents");
        setTableData([]);
        setTotal(0);
      }
    } catch (error) {
      setTableData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, columnFilters]);

  /**
   * Handle pagination change
   */
  const handlePageChange = (pagination) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  /**
   * Handle column filter change with debouncing
   */
  const handleColumnFilter = (value, field) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Handle clear all filters
   */
  const handleClearFilters = () => {
    setColumnFilters({
      deliveryAgentId: "",
      name: "",
      mobile: "",
      vehicleType: "",
      status: "",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  /**
   * Handle search input change
   */
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  /**
   * Fetch hotspots list for dropdown
   */
  const fetchHotspotsList = async () => {
    setHotspotLoading(true);
    try {
      const response = await hotspotApiService.getHotspots({
        skip: 0,
        limit: 10000,
      });
      if (response.success) {
        setHotspotsList(response.data || []);
      } else {
        message.error(response.message || "Failed to load hotspots");
        setHotspotsList([]);
      }
    } catch (error) {
      message.error(error.message || "Failed to load hotspots");
      setHotspotsList([]);
    } finally {
      setHotspotLoading(false);
    }
  };

  const fetchDeliveryPartners = async () => {
    setDeliveryPartnerLoading(true);
    try {
      const result = await API.Api(
        `${urls.DELIVERY_PARTNER_LIST}?dpType=ECOMM&limit=100000`
      ).get();
      if (
        result?.response?.data?.status === 200 &&
        result?.response?.data?.data
      ) {
        setDeliveryPartners(result?.response?.data?.data);
      } else {
        message.error(
          result?.response?.data?.message || "Failed to load delivery partners"
        );
        setDeliveryPartners([]);
      }
    } catch (error) {
      message.error(
        error?.response?.message || "Failed to fetch delivery partner details"
      );
    } finally {
      setDeliveryPartnerLoading(false);
    }
  };

  /**
   * Open edit modal
   */
  const handleEditHotspot = async (record) => {
    setSelectedDA(record);
    setIsEditModalVisible(true);
    form.resetFields();
    // Fetch the hotspots list - form population is handled by useEffect
    await fetchHotspotsList();
  };

  /**
   * Close edit modal
   */
  const handleModalCancel = () => {
    setIsEditModalVisible(false);
    setSelectedDA(null);
    form.resetFields();
  };

  /**
   * Handle hotspot assignment
   */
  const handleHotspotSubmit = async () => {
    try {
      const values = await form.validateFields();
      const response = await daApiService.updateDAHotspot({
        deliveryAgentId: selectedDA.deliveryAgentId,
        hotspotId: values.hotspotId,
        dpId: values.dpId,
        partnerType: values.daType,
      });

      if (response.success) {
        message.success(response.message || "Hotspot assigned successfully!");
        setIsEditModalVisible(false);
        setSelectedDA(null);
        form.resetFields();
        fetchDAList(); // Refresh the list
      } else {
        message.error(response.message || "Failed to assign hotspot");
      }
    } catch (error) {
      if (error.errorFields) {
        message.error("Please select a hotspot");
      } else {
        message.error("Failed to assign hotspot");
      }
    }
  };

  /**
   * Refresh data
   */
  const handleRefresh = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchDAList();
  };

  /**
   * Get column search properties for filters
   */
  const getColumnSearchProps = (dataIndex, placeholder) => {
    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search ${placeholder}...`}
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleColumnFilter(value, dataIndex);
            }}
            onPressEnter={() => {
              confirm();
            }}
            style={{
              marginBottom: 8,
              display: "block",
              borderRadius: "6px",
            }}
            allowClear={false}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                confirm();
                handleColumnFilter("", dataIndex);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <div className={`filter-icon-container ${filtered ? "filtered" : ""}`}>
          <FilterOutlined
            className={`filter-icon ${filtered ? "filtered" : ""}`}
          />
        </div>
      ),
      filteredValue: columnFilters[dataIndex]
        ? [columnFilters[dataIndex]]
        : null,
    };
  };

  // Table columns configuration with filters
  const columns = [
    {
      title: "DA ID",
      dataIndex: "deliveryAgentId",
      key: "deliveryAgentId",
      width: 100,
      render: (text) => <span className="da-id-highlight">{text}</span>,
      ...getColumnSearchProps("deliveryAgentId", "DA ID"),
    },
    {
      title: "Name",
      key: "name",
      width: 200,
      render: (_, record) => (
        <div className="da-name">
          {record.name || `${record.firstName} ${record.lastName}`}
        </div>
      ),
      //   ...getColumnSearchProps("name", "Name"),
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      width: 130,
      ...getColumnSearchProps("mobile", "Mobile"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
      //   filters: [
      //     { text: "Active", value: true },
      //     { text: "Inactive", value: false },
      //   ],
      //   filteredValue: columnFilters.status ? [columnFilters.status] : null,
      //   onFilter: (value, record) => record.status === value,
      //   filterIcon: (filtered) => (
      //     <div className={`filter-icon-container ${filtered ? "filtered" : ""}`}>
      //       <FilterOutlined
      //         className={`filter-icon ${filtered ? "filtered" : ""}`}
      //       />
      //     </div>
      //   ),
    },
    {
      title: "Vehicle Type",
      dataIndex: "vehicleType",
      key: "vehicleType",
      width: 130,
      render: (vehicleType) => <Tag color="blue">{vehicleType}</Tag>,
      //   ...getColumnSearchProps("vehicleType", "Vehicle Type"),
    },
    {
      title: "Assigned Hotspot",
      key: "assignedHotspot",
      width: 200,
      render: (_, record) => {
        const assignedHotspot = record.assignedHotspot;

        if (
          !assignedHotspot ||
          !Array.isArray(assignedHotspot) ||
          assignedHotspot.length === 0
        ) {
          return (
            <Tag color="orange" style={{ borderStyle: "dashed" }}>
              Not Assigned
            </Tag>
          );
        }

        const hotspot = assignedHotspot[0]; // Get the first (and typically only) assigned hotspot
        const assignedDate = hotspot.assignedDate
          ? new Date(hotspot.assignedDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "";

        return (
          <div className="assigned-hotspot-info">
            <div className="hotspot-name">
              <Tag color="green">
                {hotspot.hotspotName || hotspot.name || "Unnamed Hotspot"}
              </Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: "Delivery Partner",
      key: "deliveryPartner",
      width: 200,
      render: (_, record) => {
        const deliveryPartner = record?.deliveryPartner;

        if (!deliveryPartner) {
          return (
            <Tag color="orange" style={{ borderStyle: "dashed" }}>
              Not Assigned
            </Tag>
          );
        }

        return (
          <div className="delivery-partner-info">
            <div className="partner-name">
              <Tag color="blue">
                {deliveryPartner?.name ||
                  deliveryPartner?.fullName ||
                  "Unnamed Partner"}
              </Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: "DA Type",
      key: "daType",
      width: 150,
      render: (_, record) => {
        const daType = record?.daType || null;

        if (!daType) {
          return (
            <Tag color="orange" style={{ borderStyle: "dashed" }}>
              Not Assigned
            </Tag>
          );
        }

        const typeColors = {
          AREA_MANAGER: "volcano",
          NORMAL: "blue",
          SUPERVISOR: "purple",
        };

        return (
          <Tag color={typeColors[daType] || "default"}>
            {daType.replace("_", " ")}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          size="small"
          onClick={() => handleEditHotspot(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  // Custom pagination item render
  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return <a>Previous</a>;
    }
    if (type === "next") {
      return <a>Next</a>;
    }
    return originalElement;
  };

  return (
    <div className="da-hotspot-management-container">
      {/* Page Header */}
      <div style={{ marginBottom: "16px" }}>
        <Heading title="DA Hotspot Assignment" />
      </div>

      {/* Filters Card */}
      <Card className="actions-header">
        <div className="filter-row">
          {/* <div className="search-section">
            <Search
              placeholder="Search by DA name, ID, or mobile..."
              allowClear
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              enterButton={<SearchOutlined />}
            />
          </div> */}
          <div className="action-buttons">
            <Space>
              <Button
                type="default"
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                disabled={
                  !searchTerm &&
                  Object.values(columnFilters).every(
                    (val) => !val || val.trim() === ""
                  )
                }
              >
                Clear Filters
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                title="Refresh Data"
              >
                Refresh
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* DA Table */}
      <Card className="data-table-card" title="Delivery Agents List">
        <TableContainer
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} delivery agents`,
            itemRender: itemRender,
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handlePageChange}
          rowKey={(record) =>
            record.deliveryAgentId || Math.random().toString()
          }
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Edit Hotspot Modal */}
      <Modal
        className="da-hotspot-management-modal"
        title={`${
          selectedDA?.name ||
          selectedDA?.firstName + " " + selectedDA?.lastName ||
          "DA"
        }`}
        open={isEditModalVisible}
        onCancel={handleModalCancel}
        width={600}
        footer={[
          <Button key="cancel" onClick={handleModalCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={hotspotLoading}
            onClick={handleHotspotSubmit}
          >
            Update
          </Button>,
        ]}
      >
        {selectedDA && (
          <div>
            {/* DA Information */}
            <Card
              size="small"
              style={{ marginBottom: 16, background: "#f0f8ff" }}
            >
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <div>
                  <strong>DA ID:</strong> {selectedDA.deliveryAgentId}
                </div>
                <div>
                  <strong>Name:</strong>{" "}
                  {selectedDA.name ||
                    `${selectedDA.firstName} ${selectedDA.lastName}`}
                </div>
                <div>
                  <strong>Mobile:</strong> {selectedDA.mobile}
                </div>
                <div>
                  <strong>Vehicle Type:</strong> {selectedDA.vehicleType}
                </div>
              </Space>
            </Card>

            {/* Hotspot Assignment Form */}
            <Form form={form} layout="vertical">
              <Form.Item
                name="dpId"
                label="Select Delivery Partner"
                rules={[
                  {
                    required: false,
                    message: "Please select a delivery partner",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select Delivery Partner"
                  loading={deliveryPartnerLoading}
                  options={deliveryPartners.map((dp) => ({
                    value: dp?.dpId,
                    label: dp?.dpName || "Unnamed Partner",
                  }))}
                  style={{ height: "40px" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  notFoundContent={
                    deliveryPartnerLoading ? (
                      <Spin size="small" />
                    ) : (
                      "No partners found"
                    )
                  }
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="hotspotId"
                label="Select Hotspot"
                rules={[
                  {
                    required: false,
                    message: "Please select a hotspot",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select Hotspot"
                  loading={hotspotLoading}
                  options={hotspotsList.map((hotspot) => ({
                    value: hotspot._id || hotspot.id,
                    label:
                      hotspot.hotspotName || hotspot.name || "Unnamed Hotspot",
                  }))}
                  style={{ height: "40px" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  notFoundContent={
                    hotspotLoading ? <Spin size="small" /> : "No hotspots found"
                  }
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="daType"
                label="DA Type"
                rules={[
                  { required: false, message: "Please select a DA type" },
                ]}
              >
                <Select
                  placeholder="Select DA Type"
                  options={[
                    ...Object.values(DA_TYPES).map((type) => ({
                      value: type,
                      label: type.replace("_", " "),
                    })),
                  ]}
                  style={{ height: "40px" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  notFoundContent={<Spin size="small" />}
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DAHotspotManagement;
